/**
 * Groups the footer's `_at-footer-nav` accordion blocks so only one is open
 * at a time. Closes siblings via JS (`details.open = false`) instead of the
 * native `<details name="...">` exclusivity — the native browser behavior
 * snaps the previously-open panel shut with no transition, which reads as a
 * jarring jump. Closing through the same code path as a manual click lets
 * the shared accordion-custom-component transition play normally.
 */
class FooterNavGroup extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.addEventListener('toggle', this.#handleToggle, { signal: this.#controller.signal, capture: true });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {Event} event */
  #handleToggle = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLDetailsElement) || !target.open) return;

    const others = [];
    for (const details of this.querySelectorAll('details[open]')) {
      if (details instanceof HTMLDetailsElement && details !== target) others.push(details);
    }
    if (!others.length) return;

    // Closing siblings in the same tick as the browser's own open-state
    // update can get batched into one reflow, so the closing panel's
    // block-size: auto never gets committed as a transition "from" value —
    // it just snaps shut. Deferring one frame lets the browser paint the
    // newly-opened state first, so the close on the next frame animates.
    requestAnimationFrame(() => {
      others.forEach((details) => {
        details.open = false;
      });
    });
  };
}

if (!customElements.get('at-footer-nav-group')) {
  customElements.define('at-footer-nav-group', FooterNavGroup);
}
